import { cn } from "@/lib/utils";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import { useLanguage } from "@/i18n/useLanguage";
import { getTabLabelTranslation } from "@/lib/views/labels/translations";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  ListBullets,
  Package,
  CurrencyCircleDollar,
  ShoppingCart,
  Storefront,
  Receipt,
  ShieldCheck,
  Factory,
  ChartBar,
  Tag,
  LinkSimple,
  GearSix,
  FileText,
  AddressBook,
  Info,
  Notebook,
} from "@phosphor-icons/react";
import type { ComponentType } from "react";
import type { IconProps } from "@phosphor-icons/react";

/**
 * Icon + color mapping for tab grid on mobile.
 * Keys are normalized lowercase English tab labels.
 * Falls back to a generic icon for unmapped tabs.
 */
const TAB_ICON_MAP: Record<string, { icon: ComponentType<IconProps>; bg: string; text: string }> = {
  details:        { icon: ListBullets,          bg: "bg-blue-500/10",    text: "text-blue-600 dark:text-blue-400" },
  detail:         { icon: ListBullets,          bg: "bg-blue-500/10",    text: "text-blue-600 dark:text-blue-400" },
  inventory:      { icon: Package,              bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400" },
  stock:          { icon: Package,              bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400" },
  accounting:     { icon: CurrencyCircleDollar, bg: "bg-amber-500/10",  text: "text-amber-600 dark:text-amber-400" },
  purchase:       { icon: ShoppingCart,          bg: "bg-cyan-500/10",   text: "text-cyan-600 dark:text-cyan-400" },
  sales:          { icon: Storefront,            bg: "bg-rose-500/10",   text: "text-rose-600 dark:text-rose-400" },
  tax:            { icon: Receipt,               bg: "bg-orange-500/10", text: "text-orange-600 dark:text-orange-400" },
  quality:        { icon: ShieldCheck,           bg: "bg-violet-500/10", text: "text-violet-600 dark:text-violet-400" },
  manufacturing:  { icon: Factory,               bg: "bg-slate-500/10",  text: "text-slate-600 dark:text-slate-400" },
  dashboard:      { icon: ChartBar,              bg: "bg-indigo-500/10", text: "text-indigo-600 dark:text-indigo-400" },
  "item price":   { icon: Tag,                   bg: "bg-pink-500/10",   text: "text-pink-600 dark:text-pink-400" },
  connection:     { icon: LinkSimple,            bg: "bg-teal-500/10",   text: "text-teal-600 dark:text-teal-400" },
  connections:    { icon: GearSix,               bg: "bg-gray-500/10",   text: "text-gray-600 dark:text-gray-400" },
  variants:       { icon: Package,               bg: "bg-fuchsia-500/10",text: "text-fuchsia-600 dark:text-fuchsia-400" },
  terms:          { icon: FileText,              bg: "bg-stone-500/10",  text: "text-stone-600 dark:text-stone-400" },
  "address & contact": { icon: AddressBook,      bg: "bg-sky-500/10",   text: "text-sky-600 dark:text-sky-400" },
  "more info":    { icon: Info,                  bg: "bg-gray-500/10",   text: "text-gray-600 dark:text-gray-400" },
  "more information": { icon: Info,              bg: "bg-gray-500/10",   text: "text-gray-600 dark:text-gray-400" },
  "defaults & tax": { icon: Receipt,             bg: "bg-orange-500/10", text: "text-orange-600 dark:text-orange-400" },
};

const FALLBACK_STYLE = { icon: Notebook, bg: "bg-gray-500/10", text: "text-gray-600 dark:text-gray-400" };

function getTabStyle(label: string) {
  const key = label.trim().toLowerCase();
  return TAB_ICON_MAP[key] ?? FALLBACK_STYLE;
}

export function DetailPageTabBar() {
  const { tabs, activeTab, setActiveTab } = useDetailPageTabs();
  const { language } = useLanguage();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (tabs.length <= 1) return null;

  const translatedTabs = tabs.map(tab => ({
    ...tab,
    label: getTabLabelTranslation(tab.label, language),
    originalLabel: tab.label, // keep English for icon lookup
  }));

  const handleTabClick = (tabName: string) => {
    setActiveTab(tabName, false);
  };

  // Desktop: original tab bar (unchanged)
  if (isDesktop) {
    return (
      <div className="px-5 pb-3 pt-4">
        <nav className="flex flex-wrap gap-0.5">
          {translatedTabs.map((tab) => {
            const isActive = activeTab === tab.name;
            return (
              <button
                key={tab.name}
                type="button"
                onClick={() => handleTabClick(tab.name)}
                className={cn(
                  "relative px-5 h-10 text-sm whitespace-nowrap transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "touch-manipulation",
                  isActive
                    ? "text-foreground font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {tab.label}
                {isActive && (
                  <span className="absolute bottom-1 left-3 right-3 h-[2px] rounded-full bg-foreground" />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  // Mobile: super-app icon grid
  return (
    <div className="px-2 pb-3 pt-3">
      <div className="grid grid-cols-4 gap-2">
        {translatedTabs.map((tab) => {
          const isActive = activeTab === tab.name;
          const style = getTabStyle(tab.originalLabel);
          const Icon = style.icon;
          return (
            <button
              key={tab.name}
              type="button"
              onClick={() => handleTabClick(tab.name)}
              className={cn(
                "flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-xl transition-all touch-manipulation",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isActive
                  ? ""
                  : "active:bg-gray-100 dark:active:bg-gray-800"
              )}
            >
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center",
                isActive ? style.bg : "bg-gray-100 dark:bg-gray-800"
              )}>
                <Icon
                  size={22}
                  weight={isActive ? "fill" : "regular"}
                  className={isActive ? style.text : "text-gray-400 dark:text-gray-500"}
                />
              </div>
              <span className={cn(
                "text-[10px] leading-tight text-center line-clamp-2 max-w-full",
                isActive
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground"
              )}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
